// src/components/AnnotationCanvas.jsx
import React, { useRef, useState, useEffect } from "react";

export default function AnnotationCanvas({
  isOpen,
  baseImageUrl,
  overlayDataUrl,
  onChangeOverlay,
  onClose,
}) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const historyRef = useRef([]);
  const redoHistoryRef = useRef([]);
  const shapeStartRef = useRef(null);
  const baseSnapshotRef = useRef(null);
  const previousOverlayRef = useRef(null);

  // COLORS & OPACITY  (fixed defaults for now)
  const [strokeColor] = useState("#ef4444");
  const [fillColor] = useState("#ef4444");
  const [strokeOpacity] = useState(1); // 0–1
  const [fillOpacity] = useState(0); // 0–1 (0 = no fill)

  // TOOLING
  const [brushSize, setBrushSize] = useState(4);
  // Rectangles now always use sharp corners
  const [tool, setTool] = useState("pen"); // pen | eraser | line | arrow | rect | circle | text | stamp-*
  const [zoom, setZoom] = useState(1);

  // Size for the ✔ / ⚠ / ✖ stamps, tied to brush size
  const stampFontSize = Math.min(48, Math.max(18, brushSize * 5));

  const stamps = [
    { id: "stamp-ok", label: "OK", symbol: "✔" },
    { id: "stamp-warn", label: "Issue", symbol: "⚠" },
    { id: "stamp-bad", label: "Fail", symbol: "✖" },
  ];

  const brushOptions = [2, 3, 4, 6, 8, 10, 12, 16];

  const [showStrokePopup, setShowStrokePopup] = useState(false);
  const [showFillPopup, setShowFillPopup] = useState(false);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [imageSize, setImageSize] = useState(null); // real photo size once known

  const isPanningRef = useRef(false);
  const lastPanPosRef = useRef({ x: 0, y: 0 });

  // ---------- HELPERS ----------

  function hexToRgba(hex, alpha) {
    if (!hex) return `rgba(0,0,0,${alpha})`;
    let c = hex.replace("#", "");
    if (c.length === 3) {
      c = c
        .split("")
        .map((ch) => ch + ch)
        .join("");
    }
    const num = parseInt(c, 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return `rgba(${r},${g},${b},${alpha})`;
  }

  function applyStrokeStyle(ctx) {
    ctx.globalCompositeOperation = "source-over";
    ctx.strokeStyle = hexToRgba(strokeColor, strokeOpacity);
    ctx.lineWidth = brushSize;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }

  function applyShapeStyles(ctx) {
    applyStrokeStyle(ctx);
    ctx.fillStyle = hexToRgba(fillColor, fillOpacity);
  }

  function drawRoundedRect(ctx, x, y, width, height, radiusPx) {
    const r = Math.min(radiusPx, width / 2, height / 2);

    // No rounded corners – still support fill
    if (r <= 0) {
      if (fillOpacity > 0) {
        ctx.beginPath();
        ctx.rect(x, y, width, height);
        ctx.fill();
        ctx.stroke();
      } else {
        ctx.strokeRect(x, y, width, height);
      }
      return;
    }

    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + width - r, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + r);
    ctx.lineTo(x + width, y + height - r);
    ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
    ctx.lineTo(x + r, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
    if (fillOpacity > 0) ctx.fill();
    ctx.stroke();
  }

  function drawArrow(ctx, x0, y0, x1, y1) {
    const headLength = 12 + brushSize * 2;
    const dx = x1 - x0;
    const dy = y1 - y0;
    const angle = Math.atan2(dy, dx);

    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);

    ctx.moveTo(x1, y1);
    ctx.lineTo(
      x1 - headLength * Math.cos(angle - Math.PI / 6),
      y1 - headLength * Math.sin(angle - Math.PI / 6)
    );
    ctx.moveTo(x1, y1);
    ctx.lineTo(
      x1 - headLength * Math.cos(angle + Math.PI / 6),
      y1 - headLength * Math.sin(angle + Math.PI / 6)
    );
    ctx.stroke();
  }

  function placeTextLabel(text, x, y, fontSize = 20) {
    const canvas = canvasRef.current;
    if (!canvas || !text) return;
    const ctx = canvas.getContext("2d");
    ctx.save();
    ctx.globalCompositeOperation = "source-over";
    const font = `${fontSize}px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
    ctx.font = font;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    // Only fill text with strokeColor – no heavy stroke outline
    ctx.fillStyle = hexToRgba(strokeColor, strokeOpacity);
    ctx.fillText(text, x, y);
    ctx.restore();
  }

  function pushHistory(ctx, canvas) {
    try {
      const snapshot = ctx.getImageData(0, 0, canvas.width, canvas.height);
      historyRef.current.push(snapshot);
      // any new action invalidates redo stack
      redoHistoryRef.current = [];
      return snapshot;
    } catch {
      return null;
    }
  }

  // Load base image to match canvas resolution to the real photo size
  useEffect(() => {
    // If there is no base image (should be rare), fall back to a default size
    if (!baseImageUrl) {
      setImageSize({ width: 1600, height: 1000 });
      return;
    }

    const img = new Image();
    img.onload = () => {
      const width = img.naturalWidth || img.width || 1600;
      const height = img.naturalHeight || img.height || 1000;
      setImageSize({ width, height });
    };
    img.src = baseImageUrl;
  }, [baseImageUrl]);

  // ---------- INIT / ZOOM ----------
  useEffect(() => {
    if (!isOpen) return;
    if (!imageSize) return; // wait until we know the real photo size

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const width = imageSize.width || 1600;
    const height = imageSize.height || 1000;

    canvas.width = width;
    canvas.height = height;

    // Clear and reset state
    ctx.clearRect(0, 0, width, height);
    historyRef.current = [];
    redoHistoryRef.current = [];
    shapeStartRef.current = null;
    baseSnapshotRef.current = null;

    // If we already have an overlay image, draw it into the canvas
    if (overlayDataUrl) {
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
      };
      img.src = overlayDataUrl;
    }
  }, [isOpen, overlayDataUrl, imageSize]);

  // Close stroke/fill popups when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (
        !e.target.closest(".stroke-popup") &&
        !e.target.closest(".stroke-button")
      ) {
        setShowStrokePopup(false);
      }

      if (
        !e.target.closest(".fill-popup") &&
        !e.target.closest(".fill-button")
      ) {
        setShowFillPopup(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Detect mobile viewport to center toolbars on small screens
  useEffect(() => {
    if (typeof window === "undefined") return;

    function updateIsMobile() {
      // Treat any viewport <= 900px as "mobile" for toolbar layout
      setIsMobile(window.innerWidth <= 900);
    }

    updateIsMobile();
    window.addEventListener("resize", updateIsMobile);
    return () => window.removeEventListener("resize", updateIsMobile);
  }, []);

  if (!isOpen || !imageSize) return null;

  function getPos(e) {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;

    // Map screen coords to canvas coords, respecting zoom / CSS scaling
    const x = ((point.clientX - rect.left) * canvas.width) / rect.width;
    const y = ((point.clientY - rect.top) * canvas.height) / rect.height;
    return { canvas, x, y };
  }

  function changeZoom(delta) {
    setZoom((z) => {
      const next = Math.max(0.25, Math.min(3, z + delta));
      return Number(next.toFixed(2));
    });
  }

  function resetZoom() {
    setZoom(1);
  }

  // ---------- POINTER HANDLERS ----------

  function handlePointerDown(e) {
    e.preventDefault();

    // Pan tool: drag to move zoomed image
    if (tool === "pan") {
      const point = e.touches ? e.touches[0] : e;
      isPanningRef.current = true;
      lastPanPosRef.current = { x: point.clientX, y: point.clientY };
      return;
    }

    const pos = getPos(e);
    if (!pos) return;
    const { canvas, x, y } = pos;
    const ctx = canvas.getContext("2d");

    // Save history snapshot for undo *before* drawing
    const snapshot = pushHistory(ctx, canvas);

    // TEXT TOOL
    if (tool === "text") {
      const label = window.prompt("Annotation label:");
      if (label && label.trim()) {
        placeTextLabel(label.trim(), x, y);
      }
      return;
    }

    // STAMPS
    if (tool.startsWith("stamp-")) {
      const stamp = stamps.find((s) => s.id === tool);
      if (stamp) {
        placeTextLabel(stamp.symbol, x, y, stampFontSize);
      }
      return;
    }

    drawingRef.current = true;

    // FREEHAND / LINE / ERASER
    if (tool === "pen" || tool === "eraser") {
      if (tool === "eraser") {
        ctx.globalCompositeOperation = "destination-out";
        ctx.strokeStyle = "rgba(0,0,0,1)";
        ctx.lineWidth = brushSize * 2;
      } else {
        applyStrokeStyle(ctx);
      }
      ctx.beginPath();
      ctx.moveTo(x, y);
      shapeStartRef.current = null;
      baseSnapshotRef.current = null;
      return;
    }

    if (tool === "line") {
      applyStrokeStyle(ctx);
      shapeStartRef.current = { x, y };
      baseSnapshotRef.current = snapshot;
      return;
    }

    // SHAPES (arrow, rect, circle) – use snapshot for live preview
    applyShapeStyles(ctx);
    shapeStartRef.current = { x, y };
    baseSnapshotRef.current = snapshot;
  }

  function handlePointerMove(e) {
    // Pan mode
    if (tool === "pan" && isPanningRef.current) {
      e.preventDefault();
      const point = e.touches ? e.touches[0] : e;
      const dx = point.clientX - lastPanPosRef.current.x;
      const dy = point.clientY - lastPanPosRef.current.y;
      setPan((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
      lastPanPosRef.current = { x: point.clientX, y: point.clientY };
      return;
    }

    if (!drawingRef.current) return;
    e.preventDefault();
    const pos = getPos(e);
    if (!pos) return;
    const { canvas, x, y } = pos;
    const ctx = canvas.getContext("2d");

    if (tool === "pen" || tool === "eraser") {
      ctx.lineTo(x, y);
      ctx.stroke();
      return;
    }

    // SHAPES / LINE live preview
    if (!shapeStartRef.current || !baseSnapshotRef.current) return;

    ctx.putImageData(baseSnapshotRef.current, 0, 0);
    const { x: sx, y: sy } = shapeStartRef.current;

    if (tool === "line") {
      applyStrokeStyle(ctx);
      ctx.beginPath();
      ctx.moveTo(sx, sy);
      ctx.lineTo(x, y);
      ctx.stroke();
      return;
    }

    applyShapeStyles(ctx);
    const left = Math.min(sx, x);
    const top = Math.min(sy, y);
    const width = Math.abs(x - sx);
    const height = Math.abs(y - sy);

    if (tool === "rect") {
      // Always draw rectangles with sharp corners now
      drawRoundedRect(ctx, left, top, width, height, 0);
    } else if (tool === "circle") {
      const cx = left + width / 2;
      const cy = top + height / 2;
      const radiusX = width / 2;
      const radiusY = height / 2;

      ctx.beginPath();
      if (fillOpacity > 0) {
        ctx.ellipse(cx, cy, radiusX, radiusY, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      } else {
        ctx.ellipse(cx, cy, radiusX, radiusY, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
    } else if (tool === "arrow") {
      applyStrokeStyle(ctx);
      drawArrow(ctx, sx, sy, x, y);
    }
  }

  function handlePointerUp(e) {
    // End panning
    if (tool === "pan" && isPanningRef.current) {
      e.preventDefault();
      isPanningRef.current = false;
      return;
    }

    if (!drawingRef.current) return;
    e.preventDefault();
    drawingRef.current = false;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    if (tool === "eraser") {
      ctx.globalCompositeOperation = "source-over";
    }

    shapeStartRef.current = null;
    baseSnapshotRef.current = null;
  }

  // ---------- UNDO / REDO / CLEAR / DONE ----------

  function handleUndo() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    // If there is no in-session history yet, treat UNDO as
    // "revert last saved overlay" (or clear to base image).
    if (!historyRef.current.length) {
      const prevUrl = previousOverlayRef.current;

      if (prevUrl) {
        const img = new Image();
        img.onload = () => {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          historyRef.current = [];
          redoHistoryRef.current = [];
          // Update parent so next open uses the reverted overlay
          onChangeOverlay(prevUrl);
        };
        img.src = prevUrl;
        // Only allow a single cross-session step
        previousOverlayRef.current = null;
      } else {
        // No previous overlay – clear everything
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        historyRef.current = [];
        redoHistoryRef.current = [];
        onChangeOverlay(null);
      }

      return;
    }

    try {
      const current = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const previous = historyRef.current.pop();
      redoHistoryRef.current.push(current);
      if (previous) ctx.putImageData(previous, 0, 0);
    } catch {
      // ignore
    }
  }

  function handleRedo() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!redoHistoryRef.current.length) return;

    try {
      const current = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const next = redoHistoryRef.current.pop();
      historyRef.current.push(current);
      if (next) ctx.putImageData(next, 0, 0);
    } catch {
      // ignore
    }
  }

  function handleClearCanvas() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    historyRef.current = [];
    redoHistoryRef.current = [];
    shapeStartRef.current = null;
    baseSnapshotRef.current = null;
  }

  function handleDone() {
    const canvas = canvasRef.current;
    if (!canvas) {
      onClose();
      return;
    }
    let dataUrl = null;
    try {
      dataUrl = canvas.toDataURL("image/png");
    } catch {
      // ignore
    }

    // Remember what the overlay was BEFORE this save, so that
    // a later UNDO (after reopening) can revert one full step.
    previousOverlayRef.current = overlayDataUrl || null;

    onChangeOverlay(dataUrl);
    onClose();
    drawingRef.current = false;
    shapeStartRef.current = null;
    baseSnapshotRef.current = null;
  }

  // ---------- RENDER ----------

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          width: "min(1600px, 100%)",
          height: "min(1000px, 100%)",
          transform: isMobile ? "none" : "scale(0.85)",
          transformOrigin: "center center",
          borderRadius: 20,
          background: "#f9fafb",
          boxShadow: "0 18px 40px rgba(15,23,42,0.35)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* TOP BAR */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: isMobile ? "6px 12px" : "8px 20px",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              type="button"
              onClick={handleClearCanvas}
              style={{
                border: "none",
                background: "transparent",
                color: "#2563eb",
                fontSize: 13,
                padding: "2px 6px",
                cursor: "pointer",
              }}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={handleUndo}
              style={{
                border: "none",
                background: "transparent",
                color: "#2563eb",
                fontSize: 13,
                fontWeight: 600,
                padding: "2px 8px",
                cursor: "pointer",
              }}
            >
              Undo
            </button>
            <button
              type="button"
              onClick={handleRedo}
              style={{
                border: "none",
                background: "transparent",
                color: "#2563eb",
                fontSize: 13,
                fontWeight: 600,
                padding: "2px 8px",
                cursor: "pointer",
              }}
            >
              Redo
            </button>
          </div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            {new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>

          {/* Zoom group in header, like the ref screenshot */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              type="button"
              onClick={() => changeZoom(-0.25)}
              style={{
                padding: "4px 10px",
                borderRadius: 999,
                border: "1px solid #d1d5db",
                background: "transparent",
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              -
            </button>
            <div
              onClick={resetZoom}
              title="Reset zoom to 100%"
              style={{
                minWidth: 60,
                textAlign: "center",
                fontSize: 13,
                borderRadius: 999,
                border: "1px solid #d1d5db",
                padding: "4px 10px",
                background: "#ffffff",
                cursor: "pointer",
              }}
            >
              {Math.round(zoom * 100)}%
            </div>
            <button
              type="button"
              onClick={() => changeZoom(0.25)}
              style={{
                padding: "4px 10px",
                borderRadius: 999,
                border: "1px solid #d1d5db",
                background: "transparent",
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              +
            </button>
            <button
              type="button"
              onClick={handleDone}
              style={{
                marginLeft: 10,
                borderRadius: 999,
                border: "none",
                background: "#4f46e5",
                color: "#ffffff",
                fontSize: 14,
                fontWeight: 600,
                padding: "6px 16px",
                cursor: "pointer",
              }}
            >
              Done
            </button>
          </div>
        </div>

        {/* TOOLBAR */}
        <div
          style={{
            padding: isMobile ? "10px 12px 12px" : "16px 28px 20px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            justifyContent: isMobile ? "flex-start" : "space-between",
            gap: isMobile ? 12 : 16,
            fontSize: 14,
            width: "100%",
          }}
        >
          {/* ROW 1: Stroke + Fill + Line width */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: isMobile ? 12 : 24,
              flexWrap: isMobile ? "wrap" : "nowrap",
              width: isMobile ? "100%" : "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: isMobile ? 10 : 16,
                flexWrap: isMobile ? "wrap" : "nowrap",
              }}
            >
              {/* Stroke color + opacity (popup) */}
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span style={{ color: "#6b7280", fontSize: 14 }}>Stroke:</span>
                <button
                  type="button"
                  className="stroke-button"
                  onClick={() => setShowStrokePopup((v) => !v)}
                  style={{
                    width: 48,
                    height: 24,
                    borderRadius: 999,
                    border: "1px solid #d1d5db",
                    background: hexToRgba(strokeColor, strokeOpacity),
                    cursor: "pointer",
                  }}
                />

                {showStrokePopup && (
                  <div
                    className="stroke-popup"
                    style={{
                      position: "absolute",
                      top: "115%",
                      left: 0,
                      zIndex: 80,
                      background: "#ffffff",
                      borderRadius: 14,
                      border: "1px solid #e5e7eb",
                      padding: "14px 16px",
                      boxShadow: "0 16px 36px rgba(15,23,42,0.3)",
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                      minWidth: 280,
                    }}
                  >
                    {/* ... stroke popup content stays the same ... */}
                  </div>
                )}
              </div>

              {/* Fill color + opacity (popup) */}
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span style={{ color: "#6b7280", fontSize: 14 }}>Fill:</span>
                <button
                  type="button"
                  className="fill-button"
                  onClick={() => setShowFillPopup((v) => !v)}
                  style={{
                    width: 48,
                    height: 24,
                    borderRadius: 999,
                    border: "1px solid #d1d5db",
                    background:
                      fillOpacity > 0
                        ? hexToRgba(fillColor, fillOpacity)
                        : "transparent",
                    cursor: "pointer",
                    backgroundImage:
                      fillOpacity === 0
                        ? "linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 50%, #e5e7eb 50%, #e5e7eb 75%, transparent 75%, transparent)"
                        : undefined,
                    backgroundSize:
                      fillOpacity === 0 ? "8px 8px" : undefined,
                  }}
                />

                {showFillPopup && (
                  <div
                    className="fill-popup"
                    style={{
                      position: "absolute",
                      top: "115%",
                      left: 0,
                      zIndex: 80,
                      background: "#ffffff",
                      borderRadius: 14,
                      border: "1px solid #e5e7eb",
                      padding: "14px 16px",
                      boxShadow: "0 16px 36px rgba(15,23,42,0.3)",
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                      minWidth: 280,
                    }}
                  >
                    {/* ... fill popup content stays the same ... */}
                  </div>
                )}
              </div>

              {/* Line width selector – now on same row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <span style={{ color: "#6b7280", fontSize: 14 }}>
                  Line width:
                </span>
                <select
                  value={brushSize}
                  onChange={(e) => setBrushSize(Number(e.target.value))}
                  style={{
                    padding: "2px 6px",
                    borderRadius: 6,
                    border: "1px solid #d1d5db",
                    fontSize: 13,
                  }}
                >
                  {brushOptions.map((size) => (
                    <option key={size} value={size}>
                      {size}px
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* ROW 2: Tools */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: isMobile ? "center" : "flex-end",
              gap: 12,
              flexWrap: "wrap",
              width: isMobile ? "100%" : "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: isMobile ? "center" : "flex-end",
                gap: 8,
                flexWrap: "wrap",
                width: isMobile ? "100%" : "auto",
              }}
            >
              {[
                { id: "pen", label: "Pen", icon: "✏️" },
                { id: "eraser", label: "Eraser", icon: "🧽" },
                { id: "line", label: "Line", icon: "📏" },
                { id: "arrow", label: "Arrow", icon: "➤" },
                { id: "rect", label: "Rectangle", icon: "▭" },
                { id: "circle", label: "Oval", icon: "◯" },
                { id: "text", label: "Text", icon: "T" },
                { id: "pan", label: "Pan", icon: "✋" },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTool(t.id)}
                  style={{
                    padding: "3px 8px",
                    borderRadius: 8,
                    border:
                      tool === t.id
                        ? "2px solid #111827"
                        : "1px solid #d1d5db",
                    background: tool === t.id ? "#e5e7eb" : "transparent",
                    fontSize: 13,
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                  }}
                >
                  <span style={{ fontSize: 13 }}>{t.icon}</span>
                  <span>{t.label}</span>
                </button>
              ))}

              {/* Stamps directly beside Pan */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginLeft: 6,
                  flexWrap: "wrap",
                }}
              >
                {stamps.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setTool(s.id)}
                    style={{
                      padding: "3px 6px",
                      borderRadius: 8,
                      border:
                        tool === s.id
                          ? "2px solid #111827"
                          : "1px solid #d1d5db",
                      background: tool === s.id ? "#e5e7eb" : "transparent",
                      fontSize: 14,
                      color: strokeColor,
                    }}
                  >
                    {s.symbol}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* IMAGE + CANVAS AREA */}
        <div
          style={{
            flex: 1,
            padding: isMobile ? "8px 8px 10px" : "12px 16px 16px",
            background: "#111827",
            borderBottomLeftRadius: 20,
            borderBottomRightRadius: 20,
          }}
        >
          <div
            style={{
              position: "relative",
              width: "100%",
              height: "100%",
              borderRadius: 16,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                position: "relative",
                // 🔹 keep the image’s aspect ratio, fit full height of dark area
                height: "100%",
                width: "auto",
                aspectRatio: `${imageSize.width} / ${imageSize.height}`,
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: "center center",
              }}
            >
              {baseImageUrl && (
                <img
                  src={baseImageUrl}
                  alt="Annotation base"
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    pointerEvents: "none",
                  }}
                />
              )}

              <canvas
                ref={canvasRef}
                width={imageSize.width}
                height={imageSize.height}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  borderRadius: 16,
                  touchAction: "none",
                  background: "transparent",
                }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerUp}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
